// 099 D05 用户增长与使用概览：验证转化 + 筛选 + 全量列表
import { useMemo, useState } from "react";
import { ops } from "../../lib/api";
import { useOpsQuery } from "./useOpsQuery";
import { Kpi, Note, Sect, fmtMoney, fmtPct, fmtTime, fmtTokens } from "./widgets";

type Filter = "all" | "noproj" | "nogen" | "done" | "unverified";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "noproj", label: "已注册未建项目" },
  { key: "nogen", label: "已建项目未生成" },
  { key: "done", label: "已完成方案" },
  { key: "unverified", label: "未验证邮箱" },
];

export default function Users() {
  const q = useOpsQuery(() => ops.users(), []);
  const [filter, setFilter] = useState<Filter>("all");
  const [includeOps, setIncludeOps] = useState(false);
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    if (!q.data) return [];
    let r = q.data.users;
    if (!includeOps) r = r.filter((u) => !u.isOps);
    if (filter === "noproj") r = r.filter((u) => u.projects === 0);
    if (filter === "nogen") r = r.filter((u) => u.projects > 0 && u.jobs === 0);
    if (filter === "done") r = r.filter((u) => u.done > 0);
    if (filter === "unverified") r = r.filter((u) => !u.confirmed);
    const s = search.trim().toLowerCase();
    if (s) r = r.filter((u) => u.email.includes(s) || (u.displayName ?? "").toLowerCase().includes(s));
    return r;
  }, [q.data, filter, includeOps, search]);

  if (q.loading && !q.data) return <p className="muted">加载中…</p>;
  if (q.forbidden) return <p>🔒 {q.forbidden}</p>;
  if (q.error) return <p style={{ color: "var(--err)" }}>加载失败：{q.error}</p>;
  if (!q.data) return null;
  const s = q.data.summary;

  return (
    <>
      <h2 className="d-title">用户增长与使用概览</h2>
      <p className="d-sub2">注册权威：Supabase Auth · profiles 缺行不计入总数 · 邮箱仅在本授权后台展示</p>

      <div className="d-grid d-kpis" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))" }}>
        <Kpi label="累计注册用户" value={s.total} delta="Auth 创建时间去重" />
        <Kpi label="注册验证转化率" value={s.verifiedRate !== null ? fmtPct(s.verifiedRate) : "—"} delta="已验证 / 总注册" hint="评审补充" />
        <Kpi label="未验证 &gt; 48h" value={s.unverified48h} kind={s.unverified48h > 0 ? "warn" : undefined} delta="疑似验证邮件未送达，先查送达" />
        <Kpi label="运营账号" value={s.opsAccounts} delta={includeOps ? "当前已包含" : "当前已默认剔除"} />
      </div>

      <div className="d-filter">
        <div className="d-chips">
          {FILTERS.map((f) => (
            <button key={f.key} className={`d-chip${filter === f.key ? " on" : ""}`} onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
          <button className={`d-chip${includeOps ? " on" : ""}`} onClick={() => setIncludeOps(!includeOps)}>
            {includeOps ? "✓ 含运营账号" : "剔除运营账号"}
          </button>
        </div>
        <span className="d-updated">
          <input placeholder="搜索账号…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </span>
      </div>

      <div className="d-tblwrap">
        <table>
          <thead>
            <tr>
              <th>账号</th><th>注册时间</th><th>验证</th>
              <th className="num">项目数</th><th className="num">生成次数</th><th className="num">完成 / 失败</th>
              <th>最近业务活动</th><th className="num">Token</th><th className="num">估算成本</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id}>
                <td>{u.email}{u.isOps ? " 🛡" : ""}{u.displayName ? <span className="muted small">（{u.displayName}）</span> : null}</td>
                <td>{fmtTime(u.createdAt)}</td>
                <td>
                  {u.confirmed
                    ? <span className="tag t-green">已验证</span>
                    : <span className={`tag ${u.unverifiedHours > 48 ? "t-red" : "t-yellow"}`}>未验证 · {u.unverifiedHours}h</span>}
                </td>
                <td className="num">{u.projects}</td>
                <td className="num">{u.jobs}</td>
                <td className="num">{u.done} / {u.failed}</td>
                <td className={u.lastActivity ? "" : "muted"}>{u.lastActivity ? fmtTime(u.lastActivity) : "注册后无业务事件"}</td>
                <td className="num">{u.jobs ? fmtTokens(u.tokens) : "—"}</td>
                <td className="num">{u.jobs ? fmtMoney(u.costCents) : "—"}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className="muted">当前筛选无用户</td></tr>}
          </tbody>
        </table>
      </div>
      <Note>
        计数按项目/任务主键去重，一项目多任务不翻倍；「最近业务活动」取真实业务事件（建项目/发起生成），会话刷新不算活跃；
        用户维度 token/成本以 jobs 归属累计，与全站 llm_calls 台账口径分列。运营账号以 🛡 标记。
      </Note>

      <Sect title="功能使用分布" tag="评审补充" hint="上传/行业/输入完整度" />
      <div className="d-card">
        <p className="muted" style={{ margin: 0 }}>
          上传资料使用率、行业分布、输入资料完整度统计<strong>未采集</strong>——项目表单数据在 projects.form，
          但附件提取状态未单独落库；该区块待采集口径确定后补数，不编造。
        </p>
      </div>
    </>
  );
}
